import React from 'react';

export interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  description?: string;
  switchSize?: 'sm' | 'md' | 'lg';
  onCheckedChange?: (checked: boolean) => void;
}

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      label,
      description,
      switchSize = 'md',
      className = '',
      id,
      disabled = false,
      checked,
      onChange,
      onCheckedChange,
      ...props
    },
    ref
  ) => {
    const switchId = id || `switch-${Math.random().toString(36).substring(2, 9)}`;

    const sizeConfig = {
      sm: {
        track: 'h-5 w-9',
        thumb: 'h-4 w-4',
        translate: 'translate-x-4',
      },
      md: {
        track: 'h-6 w-11',
        thumb: 'h-5 w-5',
        translate: 'translate-x-5',
      },
      lg: {
        track: 'h-7 w-14',
        thumb: 'h-6 w-6',
        translate: 'translate-x-7',
      },
    };

    const config = sizeConfig[switchSize];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      onCheckedChange?.(e.target.checked);
    };

    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-labelledby={label ? `${switchId}-label` : undefined}
          disabled={disabled}
          onClick={() => {
            const input = document.getElementById(switchId) as HTMLInputElement;
            input?.click();
          }}
          className={`
            relative inline-flex items-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
            ${config.track}
            ${checked ? 'bg-primary' : 'bg-gray-300'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <span className="sr-only">{label || 'Toggle switch'}</span>
          <span
            className={`
              inline-block rounded-full bg-white shadow-lg transform transition-transform duration-200
              ${config.thumb}
              ${checked ? config.translate : 'translate-x-0.5'}
            `}
          />
        </button>

        <input
          ref={ref}
          type="checkbox"
          id={switchId}
          className="sr-only"
          disabled={disabled}
          checked={checked}
          onChange={handleChange}
          {...props}
        />

        {(label || description) && (
          <label
            id={`${switchId}-label`}
            htmlFor={switchId}
            className={`flex-1 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          >
            {label && (
              <span className="block text-sm font-medium text-gray-900">
                {label}
              </span>
            )}
            {description && (
              <span className="block text-sm text-gray-600 mt-0.5">
                {description}
              </span>
            )}
          </label>
        )}
      </div>
    );
  }
);

Switch.displayName = 'Switch';
